import React, {useState, useMemo} from "react";
import { MediaCategory, MediaWithCategories } from "../../../types/Media";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import {faXmark} from "@fortawesome/free-solid-svg-icons";
import Icon from "../../../components/Icon";
import Card from "../../../components/Card";
import Select from "../../../components/Select";
import styles from "./mediaOverview.module.css";
import {fabric} from "fabric";
import {generateClipPath} from "../../../helpers/generateClipPath";
import Heading from "../../../components/Heading";
import {usePagination} from "../../../components/Pagination/usePagination";
import Pagination from "../../../components/Pagination";
import {treeOptions} from "../../../helpers/treeOptions";
import {useCustomizer} from "../../../contexts/CustomizerContext";
import {getTranslatedObject} from "../../../helpers/getTranslatedObject";
import {useGlobalContext} from "../../../contexts/globalContext";

const MediaOverview = () => {
    const { t } = useTranslation(['customizer']);
    const {plugin} = useGlobalContext();
    const {
        closeMediaOverview,
        canvas,
        getClipPath,
        spotInMediaOverview,
        activeTemplateId,
        setUserInputs,
        media,
        categories,
        isMediaLoading,
        activeTemplate
    } = useCustomizer();

    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

    const translatedCategories = useMemo(() => {
        if (categories) return categories.map(category => getTranslatedObject(category, plugin?.language));
        return [];
    }, [categories, plugin]);

    const filteredMedia: MediaWithCategories[] = useMemo(() => {
        return selectedCategoryIds.length > 0
            ? media.filter(mediaItem =>
                mediaItem.media_category_ids.some(categoryId =>
                    selectedCategoryIds.includes(categoryId)

                )
            )
            : media;
    }, [media, selectedCategoryIds]);

    const filteredMediaBySpotCategories = useMemo(() => {
        const activeSpot = activeTemplate?.spots.find(spot => spot.id === spotInMediaOverview?.[0]);
        if (!activeSpot || !activeSpot.media_category_filtered) return filteredMedia;
        return filteredMedia.filter(mediaItem => {
          return activeSpot.media_category_ids.some(categoryId =>
            mediaItem.media_category_ids.includes(categoryId)
          );
        })
    }, [filteredMedia, activeTemplate, spotInMediaOverview]);

    const onCategorySelect = (categoryId: number) => {
        setSelectedCategoryIds(prevCategoryIds => {
            if (prevCategoryIds.includes(categoryId)) return prevCategoryIds;
            else return [...prevCategoryIds, categoryId];
        });
    };

    const onCategoryRemove = (categoryId: number) => {
        setSelectedCategoryIds(prevCategoryIds =>
            prevCategoryIds.filter(id => id !== categoryId)
        );
    };

    const selectImage = (path: string) => {
        if(spotInMediaOverview === null) return;
        const clipPath = getClipPath(spotInMediaOverview[0]);
        if (!clipPath) return;

        canvas.getObjects().forEach(obj => {
            if (obj.data?.unique_id === spotInMediaOverview[1]) {
                canvas.remove(obj)
            }
        });

        fabric.Image.fromURL(path, (fabricImage: fabric.Image) => {
            const factor = fabricImage.getOriginalSize().height / fabricImage.getOriginalSize().width;

            const img = fabricImage.set({
                left: clipPath.left,
                top: clipPath.top,
                centeredScaling: true,
                originX: 'center',
                originY: 'center',
                hoverCursor: 'initial',
                selectable: true,
                hasControls: true,
                hasBorders: true,
                borderColor: '#FC0',
                cornerColor: '#FC0',
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerSize: 12,
                hasRotatingPoint: true,
                snapAngle: 1,
                data: {
                    unique_id: spotInMediaOverview[1],
                    spot_id: spotInMediaOverview[0],
                    template_id: activeTemplateId,
                    size: clipPath.data.size,
                    is_clip_path: false
                }
            });

            img.setControlsVisibility({
                tl: true,
                tr: true,
                br: true,
                bl: true,
                ml: false,
                mt: false,
                mr: false,
                mb: false,
                mtr: true
            });

            img.scaleToWidth(clipPath.width ?? 100);
            img.scaleToHeight((clipPath.width ?? 100) * factor);

            img.set('clipPath', generateClipPath(clipPath));

            setUserInputs(prev => new Map(prev.set(spotInMediaOverview[1], img)));
            canvas.add(img);
            canvas.setActiveObject(img);
        });
        closeMediaOverview();
    };

    const selectOptions = useMemo(() => {
        const activeSpot = activeTemplate?.spots.find(spot => spot.id === spotInMediaOverview?.[0]);
        if (!activeSpot || !activeSpot.media_category_filtered) {
            return treeOptions<MediaCategory>(
                translatedCategories,
                null,
                'name',
                'id',
                'parent_id',
                'id',
                0
            );
        }
        const spotCategoryIds = activeSpot.media_category_ids.map(id => Number(id));

        const filteredCategories = translatedCategories.filter(category => {
            const categoryId = Number(category.id);
            const isCategorySelected = spotCategoryIds.includes(categoryId);
            return isCategorySelected;
        });

        if (activeSpot && activeSpot.media_category_filtered) {
            return filteredCategories.map(category => ({
                label: category.name,
                value: String(category.id)
            }));
        }

        return treeOptions<MediaCategory>(
            translatedCategories,
            null,
            'name',
            'id',
            'parent_id',
            'id',
            0
        );
    }, [translatedCategories, activeTemplate, spotInMediaOverview]);

    const {
        currentPage,
        handlePageChange,
        totalPages,
        startIndex,
        endIndex
    } = usePagination(1, filteredMedia.length, 6);

    return spotInMediaOverview ? (
        <div className={cx(styles['media-overview-wrapper'])}>
            <>
                <Heading level={3}>{t('mediaLibrary')}</Heading>
                <div className={cx(styles['media-filter-category'])}>
                    <Select
                        options={selectOptions}
                        onSelect={(property: string, value: string) => onCategorySelect(parseInt(value))}
                        property="category"
                        value={""}
                        placeholder={t('mediaFilterByCategory')}
                        classes={'mr-4 w-25 mb-1 w-auto sm:w-100 sm:mb-0'}
                        searchable={true}
                        searchTermUpdate={true}
                    />
                    <div className={cx(styles['selected-categories-filter'])}>
                        {selectedCategoryIds.map(id => {
                            const category = translatedCategories.find(cat => cat.id === id);
                            return (
                                <div key={id} className={cx(styles['selected-categories-elements-filter'])}>
                                    <span>{category?.name}</span>
                                    <Icon
                                        icon={faXmark}
                                        color={'#2f2f2f'}
                                        onClick={() => onCategoryRemove(id)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <Icon classes={cx(styles['close-button'])} icon={faXmark} onClick={closeMediaOverview} />
                <div className={cx(styles['media-content-wrapper'])}>
                    {isMediaLoading && <p>Loading media...</p>}
                    {!isMediaLoading && filteredMediaBySpotCategories.slice(startIndex, endIndex).map(mediaItem => (
                        <Card onClick={() => selectImage(mediaItem.path)} key={mediaItem.id} classes={cx(styles['card-wrapper'])}>
                            <div className={cx(styles['card-inner-wrapper'])}>
                                <div className={cx(styles['media-wrapper'])}>
                                    <img src={mediaItem.path} alt={mediaItem.name} height={150} width={150} style={{objectFit : "contain"}} />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
                <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
            </>
        </div>
    ) : null;
};

export default MediaOverview;
